import type {
  ArtifactWorkflow,
  ArtifactWorkflowNode,
  ArtifactWorkflowNodeStatus,
  ArtifactWorkflowOperationPhase,
  SessionArtifactRecord,
} from "./types";
import {
  evaluateValidationRule,
  type ValidationRuleEvaluationInput,
} from "@granular-software/metamodel-validation-rule";

type ArtifactLike = Omit<SessionArtifactRecord, "workflow"> & {
  workflow?: ArtifactWorkflow | null;
};

const TERMINAL_ARTIFACT_STATUSES = new Set([
  "completed",
  "partially_completed",
  "failed",
  "canceled",
]);

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function humanize(value: string): string {
  const words = value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : "Details";
}

function stableToken(value: string): string {
  return encodeURIComponent(value.trim().toLowerCase());
}

function isMissing(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "string" && !value.trim()) ||
    (Array.isArray(value) && value.length === 0)
  );
}

function displayValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(displayValue).filter(Boolean).join(", ");
  }
  return "Provided";
}

function artifactFields(
  artifact: ArtifactLike,
): Array<Record<string, unknown>> {
  return Array.isArray(artifact.inputSchema?.fields)
    ? artifact.inputSchema.fields.filter(
        (field): field is Record<string, unknown> => Boolean(record(field)),
      )
    : [];
}

function declaredSections(artifact: ArtifactLike): Array<{
  id: string;
  label: string;
  description: string | null;
  fieldNames: string[];
}> {
  const fields = artifactFields(artifact);
  if (!fields.length) return [];
  const knownNames = new Set(
    fields
      .map((field) => text(field.name))
      .filter((name): name is string => Boolean(name)),
  );
  const ui = record(artifact.inputSchema?.ui);
  const workflow = record(ui?.workflow);
  const rawSections = Array.isArray(workflow?.steps)
    ? workflow.steps
    : Array.isArray(ui?.sections)
      ? ui.sections
      : [];
  const used = new Set<string>();
  const sections = rawSections.flatMap((value) => {
    const section = record(value);
    const id = text(section?.id);
    const label = text(section?.label);
    const fieldNames = Array.isArray(section?.fieldNames)
      ? section.fieldNames
          .map(text)
          .filter(
            (name): name is string =>
              name !== null && knownNames.has(name) && !used.has(name),
          )
      : [];
    if (!id || !label || !fieldNames.length) return [];
    fieldNames.forEach((name) => used.add(name));
    return [{ id, label, description: text(section?.description), fieldNames }];
  });
  const remaining = [...knownNames].filter((name) => !used.has(name));
  const chunkSize = 5;
  const chunkCount = Math.ceil(remaining.length / chunkSize);
  const hasDeclaredSections = sections.length > 0;
  for (let index = 0; index < chunkCount; index += 1) {
    const baseLabel = hasDeclaredSections ? "Other details" : "Details";
    sections.push({
      id:
        chunkCount === 1
          ? hasDeclaredSections
            ? "other-details"
            : "details"
          : `${hasDeclaredSections ? "other-details" : "details"}-${index + 1}`,
      label: chunkCount === 1 ? baseLabel : `${baseLabel} ${index + 1}`,
      description: null,
      fieldNames: remaining.slice(index * chunkSize, (index + 1) * chunkSize),
    });
  }
  return sections;
}

function fieldValue(artifact: ArtifactLike, fieldName: string): unknown {
  const field = artifactFields(artifact).find(
    (candidate) => text(candidate.name) === fieldName,
  );
  const kind = text(field?.kind);
  return kind === "relationshipOne" || kind === "relationshipMany"
    ? artifact.relationships?.[fieldName]
    : artifact.inputValues?.[fieldName];
}

function conditionallyRequired(
  artifact: ArtifactLike,
  field: Record<string, unknown>,
): boolean {
  const raw = field.requiredWhen ?? field.required_when;
  const conditions = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return (
    conditions.length > 0 &&
    conditions.every((value) => {
      const condition = record(value);
      const dependency = text(condition?.field);
      return (
        Boolean(dependency) &&
        Object.prototype.hasOwnProperty.call(condition || {}, "equals") &&
        Object.is(fieldValue(artifact, dependency!), condition!.equals)
      );
    })
  );
}

function fieldValidationRules(
  field: Record<string, unknown>,
): ValidationRuleEvaluationInput[] {
  const raw = Array.isArray(field.validationRules)
    ? field.validationRules
    : Array.isArray(field.validation_rules)
      ? field.validation_rules
      : Array.isArray(field.validate)
        ? field.validate
        : [];
  return raw.filter(
    (rule): rule is ValidationRuleEvaluationInput =>
      Boolean(record(rule)) && typeof record(rule)?.operator === "string",
  );
}

function fieldProvenanceNeedsReview(
  artifact: ArtifactLike,
  field: Record<string, unknown>,
): boolean {
  const fieldName = text(field.name);
  if (!fieldName) return false;
  const namespace = text(field.kind)?.startsWith("relationship")
    ? "relationships"
    : "inputValues";
  const provenance = artifact.fieldProvenance?.[`${namespace}.${fieldName}`];
  if (!provenance) return false;
  const confirmedAt =
    typeof provenance.confirmedAt === "number" ? provenance.confirmedAt : null;
  const updatedAt =
    typeof provenance.updatedAt === "number" ? provenance.updatedAt : null;
  const invalidatedAt =
    typeof provenance.invalidatedAt === "number"
      ? provenance.invalidatedAt
      : null;
  const explicitlyConfirmed =
    confirmedAt !== null &&
    (updatedAt === null || confirmedAt >= updatedAt) &&
    (invalidatedAt === null || confirmedAt >= invalidatedAt);
  if (explicitlyConfirmed) return false;
  const invalidated =
    invalidatedAt !== null &&
    (confirmedAt === null || confirmedAt < invalidatedAt);
  const assistantSource = [
    "user_statement",
    "agent_extraction",
    "agent_inference",
    "provider_lookup",
    "ontology_default",
  ].includes(provenance.source);
  return (
    invalidated || (assistantSource && provenance.confidence === "ambiguous")
  );
}

function sectionComplete(
  artifact: ArtifactLike,
  fieldNames: string[],
): boolean {
  const schema = record(artifact.inputSchema);
  const declaredRequiredNames = new Set(
    [
      ...(Array.isArray(schema?.required) ? schema.required : []),
      ...(Array.isArray(schema?.["x-granular-artifact-required"])
        ? schema["x-granular-artifact-required"]
        : []),
    ]
      .map(text)
      .filter((name): name is string => Boolean(name)),
  );
  const validation = record(artifact.validation);
  const invalidFieldNames = new Set(
    (Array.isArray(validation?.issues) ? validation.issues : [])
      .map(record)
      .filter((issue): issue is Record<string, unknown> => Boolean(issue))
      .map((issue) => text(issue.path) || text(issue.field))
      .filter((path): path is string => Boolean(path))
      .map((path) => path.replace(/^(inputValues|relationships)\./, "")),
  );
  const fieldsByName = new Map(
    artifactFields(artifact).map((field) => [text(field.name), field]),
  );
  return fieldNames.every((fieldName) => {
    if (invalidFieldNames.has(fieldName)) return false;
    const field = fieldsByName.get(fieldName);
    if (!field) return true;
    if (fieldProvenanceNeedsReview(artifact, field)) return false;
    const required =
      declaredRequiredNames.has(fieldName) ||
      field.required === true ||
      conditionallyRequired(artifact, field);
    const value = fieldValue(artifact, fieldName);
    const provenance =
      artifact.fieldProvenance?.[
        `${text(field.kind)?.startsWith("relationship") ? "relationships" : "inputValues"}.${fieldName}`
      ];
    if (provenance?.invalidatedAt) return false;
    if (text(field.kind)?.startsWith("relationship")) {
      const resolvers = record(
        record(artifact.metadata)?.relationshipResolvers,
      );
      const resolverStatus = text(record(resolvers?.[fieldName])?.status);
      // A resolver in `choice` mode means a person must decide *when the
      // relationship is empty*. Once a concrete record is selected, that
      // selection is the decision; keeping the section incomplete until some
      // unrelated later submit makes the navigation contradict the form.
      // `missing` remains an unconditional blocker because it says the provider
      // could not resolve the declared relationship at all.
      if (resolverStatus === "missing") return false;
      if (resolverStatus === "choice" && isMissing(value)) return false;
    }
    if (required && isMissing(value)) return false;
    if (isMissing(value)) return true;
    return fieldValidationRules(field).every(
      (rule) => evaluateValidationRule(value, rule).passed,
    );
  });
}

function sectionRecap(artifact: ArtifactLike, fieldNames: string[]) {
  const fields = artifactFields(artifact);
  const fieldsByName = new Map(
    fields.map((field) => [text(field.name), field]),
  );
  const labels = new Map(
    fields.map((field) => [
      text(field.name),
      text(field.label) || humanize(text(field.name) || "detail"),
    ]),
  );
  const items = fieldNames.flatMap((fieldName) => {
    const value = fieldValue(artifact, fieldName);
    if (isMissing(value)) return [];
    const field = fieldsByName.get(fieldName);
    const relationshipLabels = record(
      record(artifact.metadata)?.relationshipLabels,
    );
    const fieldLabels = record(relationshipLabels?.[fieldName]);
    const resolvers = record(record(artifact.metadata)?.relationshipResolvers);
    const candidates = Array.isArray(record(resolvers?.[fieldName])?.candidates)
      ? (record(resolvers?.[fieldName])?.candidates as unknown[])
      : [];
    const candidateLabel = candidates
      .map(record)
      .find((candidate) => text(candidate?.id) === value)?.label;
    const rawOptions = Array.isArray(field?.enumOptions)
      ? field.enumOptions
      : Array.isArray(field?.options)
        ? field.options
        : [];
    const enumLabel = rawOptions
      .map((option) =>
        typeof option === "string"
          ? { value: option, label: humanize(option) }
          : record(option),
      )
      .find((option) => text(option?.value) === value)?.label;
    const rendered =
      typeof value === "string" && text(fieldLabels?.[value])
        ? text(fieldLabels?.[value])!
        : typeof value === "string" && text(candidateLabel)
          ? text(candidateLabel)!
          : typeof value === "string" && text(enumLabel)
            ? text(enumLabel)!
            : typeof value === "string" &&
                (text(field?.kind) === "enum" ||
                  text(field?.kind)?.startsWith("relationship"))
              ? humanize(value)
              : displayValue(value);
    return [
      { label: labels.get(fieldName) || humanize(fieldName), value: rendered },
    ];
  });
  return {
    summary: items.length
      ? items.map((item) => `${item.label}: ${item.value}`).join(" · ")
      : "Completed",
    items,
  };
}

function operationPhase(
  artifact: ArtifactLike,
): ArtifactWorkflowOperationPhase | null {
  const execution = record(record(artifact.metadata)?.execution);
  const phase = text(execution?.phase);
  if (artifact.status === "completed") return "completed";
  if (artifact.status === "failed") return "failed";
  if (artifact.status === "awaiting_human") return "waiting_for_approval";
  if (phase === "preparing") return "reviewing";
  if (phase === "syncing_record") return "synchronizing";
  if (phase === "verifying_target") return "verifying";
  if (artifact.status === "running") return "sending";
  return null;
}

function operationLabel(artifact: ArtifactLike): string {
  const ui = record(artifact.inputSchema?.ui);
  const workflow = record(ui?.workflow);
  const effect = record(record(artifact.metadata)?.effect);
  return (
    text(workflow?.operationLabel) ||
    text(ui?.submitLabel) ||
    text(effect?.activityLabel) ||
    text(effect?.label) ||
    artifact.label
  );
}

function relationshipValueContainsRecordId(
  value: unknown,
  recordId: string,
): boolean {
  return Array.isArray(value)
    ? value.some((item) => item === recordId)
    : value === recordId;
}

/**
 * A related record can finish before the parent relationship has received the
 * provider commit receipt. That is real work still in flight, not a finished
 * relationship and not a reason to make the structural parent actionable.
 * Keep the child visible as a synchronizing leaf until the parent contains
 * evidence for this exact child result.
 */
function relationshipChildLinkPending(
  parent: ArtifactLike,
  fieldName: string,
  child: ArtifactLike,
): boolean {
  if (child.status !== "completed") return false;
  const parentMetadata = record(parent.metadata);
  const linkMarker = record(
    record(parentMetadata?.relationshipLinkProvenance)?.[fieldName],
  );
  const relationshipValue = parent.relationships?.[fieldName];
  const childExecution = record(record(child.metadata)?.execution);
  const childResult = record(childExecution?.result);
  const childCommit = record(childResult?.commit);
  const primaryTarget = record(childCommit?.primaryTarget);
  const committedRecordId = text(primaryTarget?.id);
  const markerRecordId = text(linkMarker?.recordId);
  const exactRecordId = markerRecordId || committedRecordId;
  if (exactRecordId) {
    return !relationshipValueContainsRecordId(relationshipValue, exactRecordId);
  }
  // Fixtures and partially populated snapshots may not carry a commit
  // receipt. An empty relationship is still unambiguously pending; a
  // non-empty relationship is the only available evidence that it is linked.
  return isMissing(relationshipValue);
}

function stateTransitionSteps(artifact: ArtifactLike): Array<{
  index: number;
  transition: string;
  label: string;
  description: string | null;
  status: string;
  userVisible: boolean;
  blockers: Array<Record<string, unknown>>;
}> {
  if (artifact.kind !== "state_path") return [];
  const statePlan = record(record(artifact.metadata)?.statePlan);
  const plan = record(statePlan?.plan);
  const transitionMetadata = record(statePlan?.transitionMetadata);
  const rawSteps = Array.isArray(statePlan?.steps)
    ? statePlan.steps
    : Array.isArray(plan?.steps)
      ? plan.steps
      : [];
  // Automerge list proxies satisfy Array.isArray() but do not necessarily
  // expose Array.prototype methods. Materialize the read-only sequence before
  // projecting it so the same code accepts both canonical JSON and an
  // Automerge-backed client document.
  return Array.from(rawSteps).flatMap((value, fallbackIndex) => {
    const step = record(value);
    const transition = text(step?.transition);
    if (!step || !transition) return [];
    const transitionDescription = record(transitionMetadata?.[transition]);
    const userVisible =
      typeof step.userVisible === "boolean"
        ? step.userVisible
        : typeof transitionDescription?.userVisible === "boolean"
          ? transitionDescription.userVisible
          : true;
    const blockers = Array.isArray(step.blockers)
      ? step.blockers.filter((blocker): blocker is Record<string, unknown> =>
          Boolean(record(blocker)),
        )
      : [];
    return [
      {
        index: typeof step.index === "number" ? step.index : fallbackIndex,
        transition,
        label: text(step.label) || humanize(transition),
        description:
          text(step.description) ||
          text(record(step.expectedOutcome)?.summary) ||
          null,
        status: text(step.status) || "pending",
        // State plans may contain bookkeeping transitions that are necessary
        // for execution/audit but are already represented by an actionable
        // form or by the single final operation. Producers must opt those
        // transitions out explicitly; the client never infers this from a
        // label or transition name.
        userVisible,
        blockers,
      },
    ];
  });
}

function stateTransitionGroupLabel(artifact: ArtifactLike): string | null {
  if (artifact.kind !== "state_path") return null;
  const statePlan = record(record(artifact.metadata)?.statePlan);
  const classLabel = text(statePlan?.classLabel);
  return classLabel ? `${classLabel} workflow` : null;
}

function workflowGroupStatus(
  nodes: readonly ArtifactWorkflowNode[],
): ArtifactWorkflowNodeStatus {
  const active = nodes.find((node) =>
    ["current", "needs_input", "working", "blocked", "failed"].includes(
      node.status,
    ),
  );
  if (active) return active.status;
  // A branch with completed history and future leaves is not complete. This
  // commonly occurs when an earlier editor still gates the state machine: the
  // parent should recede with its first unfinished child, not show a green
  // completion check merely because it has a historical terminal child.
  if (nodes.some((node) => node.status === "upcoming")) return "upcoming";
  const lastTerminal = [...nodes]
    .reverse()
    .find((node) => isTerminalWorkflowStepStatus(node.status));
  return lastTerminal?.status || nodes[0]?.status || "upcoming";
}

function transitionNodeStatus(
  artifact: ArtifactLike,
  step: ReturnType<typeof stateTransitionSteps>[number],
): ArtifactWorkflowNodeStatus {
  const statePlan = record(record(artifact.metadata)?.statePlan);
  const currentStep = record(statePlan?.currentStep);
  const execution = record(record(artifact.metadata)?.execution);
  const currentTransition = text(execution?.currentTransition);
  const completedTransitions = new Set(
    Array.isArray(execution?.completedTransitions)
      ? execution.completedTransitions
          .map(text)
          .filter((value): value is string => Boolean(value))
      : [],
  );
  const isCurrent =
    currentTransition === step.transition || currentStep?.index === step.index;
  if (
    completedTransitions.has(step.transition) ||
    step.status === "completed"
  ) {
    return "completed";
  }
  if (isCurrent && artifact.status === "failed") return "failed";
  if (isCurrent && artifact.status === "canceled") return "canceled";
  if (isCurrent && artifact.status === "running") return "working";
  if (step.status === "failed") return "failed";
  // A canceled artifact cannot leave an unfinished state-path step looking
  // actionable. Completed history remains completed above; every other step
  // is terminal cancellation and the navigator will correctly settle on the
  // final recap instead of a dead-end current leaf.
  if (artifact.status === "canceled") return "canceled";
  if (step.status === "blocked") return "blocked";
  if (step.status === "needs_other_user" || step.status === "needs_input") {
    return "needs_input";
  }
  if (isCurrent || step.status === "ready" || step.status === "current") {
    return "current";
  }
  if (step.status === "skipped") return "skipped";
  if (step.status === "canceled") return "canceled";
  return "upcoming";
}

const TERMINAL_WORKFLOW_STEP_STATUSES = new Set<ArtifactWorkflowNodeStatus>([
  "completed",
  "skipped",
  "canceled",
]);

export function isArtifactWorkflowNodeTerminal(
  nodeOrStatus: ArtifactWorkflowNode | ArtifactWorkflowNodeStatus,
): boolean {
  const status =
    typeof nodeOrStatus === "string" ? nodeOrStatus : nodeOrStatus.status;
  return TERMINAL_WORKFLOW_STEP_STATUSES.has(status);
}

function isTerminalWorkflowStepStatus(
  status: ArtifactWorkflowNodeStatus,
): boolean {
  return isArtifactWorkflowNodeTerminal(status);
}

/**
 * A plan can contain a `pending` step without explicitly repeating which
 * step is actionable. Once all form sections are ready, the first non-terminal
 * step is the only safe place to put the user. Later `ready`, `blocked`, or
 * `needs_input` flags must not make the client jump over that step.
 */
function firstTransitionStatus(
  artifact: ArtifactLike,
  status: ArtifactWorkflowNodeStatus,
): ArtifactWorkflowNodeStatus {
  if (status !== "upcoming") return status;
  if (artifact.status === "canceled") return "canceled";
  if (artifact.status === "failed") return "failed";
  if (
    artifact.status === "blocked" ||
    artifact.status === "stale" ||
    artifact.status === "partially_completed"
  ) {
    return "blocked";
  }
  if (artifact.status === "running") return "working";
  if (artifact.status === "awaiting_human") return "needs_input";
  return "current";
}

function operationStatus(
  artifact: ArtifactLike,
  transitionStatuses: readonly ArtifactWorkflowNodeStatus[] = [],
): ArtifactWorkflowNodeStatus {
  if (artifact.status === "completed") {
    // A terminal artifact status is not enough when the authoritative state
    // path still contains work. Keep the surface open for stale or partially
    // written snapshots instead of offering a false completion receipt.
    if (
      transitionStatuses.some(
        (status) => !TERMINAL_WORKFLOW_STEP_STATUSES.has(status),
      )
    ) {
      if (transitionStatuses.includes("failed")) return "failed";
      if (transitionStatuses.includes("needs_input")) return "needs_input";
      if (transitionStatuses.includes("working")) return "working";
      if (transitionStatuses.includes("current")) return "current";
      return "blocked";
    }
    return "completed";
  }
  if (artifact.status === "failed") return "failed";
  if (artifact.status === "canceled") return "canceled";
  if (artifact.status === "partially_completed") return "blocked";
  if (artifact.status === "blocked" || artifact.status === "stale")
    return "blocked";
  if (artifact.status === "running") return "working";
  if (artifact.status === "awaiting_human") return "needs_input";
  if (artifact.status === "ready") return "current";
  return "upcoming";
}

function semanticWorkflow(workflow: ArtifactWorkflow): unknown {
  const { revision: _revision, updatedAt: _updatedAt, ...semantic } = workflow;
  return semantic;
}

function isNavigationOnlyOperation(
  workflow: ArtifactWorkflow,
  node: ArtifactWorkflowNode,
): boolean {
  const hasExplicitTransitionLeaf = getArtifactWorkflowNodes(workflow).some(
    (candidate) =>
      candidate.childIds.length === 0 && candidate.kind === "transition",
  );
  return (
    hasExplicitTransitionLeaf &&
    node.childIds.length === 0 &&
    node.kind === "operation" &&
    // A terminal operation is redundant once authored state-path leaves are
    // available. While it is still delivering/synchronizing, however, it is
    // the only honest representation of the work still in progress.
    isArtifactWorkflowNodeTerminal(node)
  );
}

export function getArtifactWorkflowNavigationLeaves(
  workflow: ArtifactWorkflow,
): ArtifactWorkflowNode[] {
  return getArtifactWorkflowNodes(workflow).filter((node) =>
    isArtifactWorkflowNodeNavigableLeaf(workflow, node),
  );
}

export function isArtifactWorkflowNodeNavigableLeaf(
  workflow: ArtifactWorkflow,
  node: ArtifactWorkflowNode,
): boolean {
  return (
    node.childIds.length === 0 && !isNavigationOnlyOperation(workflow, node)
  );
}

function isArtifactWorkflowNodeActionable(node: ArtifactWorkflowNode): boolean {
  return (
    node.availability === "available" &&
    ["current", "needs_input", "working", "blocked", "failed"].includes(
      node.status,
    )
  );
}

/**
 * Resolve the only automatic destination allowed by the workflow contract.
 * The first non-terminal actionable leaf owns the next user interaction. A
 * terminal workflow has no active leaf, so the last leaf remains the stable
 * recap destination. This selector is shared by the SDK projector and UI;
 * neither caller is allowed to infer a destination from labels or row order.
 */
export function getArtifactWorkflowNavigationTarget(
  workflow: ArtifactWorkflow,
): ArtifactWorkflowNode | null {
  const leaves = getArtifactWorkflowNavigationLeaves(workflow);
  return (
    leaves.find(
      (node) =>
        !isArtifactWorkflowNodeTerminal(node) &&
        isArtifactWorkflowNodeActionable(node),
    ) ||
    leaves.at(-1) ||
    null
  );
}

export function projectArtifactWorkflow(
  artifact: ArtifactLike,
  allArtifacts: readonly ArtifactLike[] = [artifact],
  now = artifact.updatedAt,
): ArtifactWorkflow {
  const nodesById: Record<string, ArtifactWorkflowNode> = {};
  const rootNodeIds: string[] = [];
  const sections = declaredSections(artifact);
  const children = new Map(
    allArtifacts
      .filter((candidate) => candidate.parentArtifactId === artifact.artifactId)
      .map((candidate) => [candidate.artifactId, candidate]),
  );
  const relationshipChildren = record(
    record(artifact.metadata)?.relationshipChildren,
  );
  const operationId = `${artifact.artifactId}:operation`;
  const validationId = `${artifact.artifactId}:validation`;
  const sectionNodeIds: string[] = [];
  let currentNodeId: string | null = null;
  let foundIncompleteSection = false;
  let foundActiveLeaf = false;
  let transitionHasActiveStep = false;
  let hasVisibleTransition = false;
  const projectedTransitionStatuses: ArtifactWorkflowNodeStatus[] = [];
  const transitionNodeIds: string[] = [];
  const transitionGroupLabel = stateTransitionGroupLabel(artifact);
  const transitionGroupId = `${artifact.artifactId}:transition-group`;

  for (const section of sections) {
    const id = `${artifact.artifactId}:section:${stableToken(section.id)}`;
    const complete = sectionComplete(artifact, section.fieldNames);
    const blockedByEarlierSection = foundIncompleteSection;
    const blockedByEarlierAction = foundIncompleteSection || foundActiveLeaf;
    const relationshipFields = artifactFields(artifact).filter(
      (field) =>
        section.fieldNames.includes(text(field.name) || "") &&
        ["relationshipOne", "relationshipMany"].includes(
          text(field.kind) || "",
        ),
    );
    const relationshipField = relationshipFields[0];
    // A section may contain several relationships. The relationship that owns
    // a nested artifact is the one named by relationshipChildren; choosing the
    // first relationship field makes a nested vendor/receipt disappear when a
    // section also contains cost center or budget fields.
    const relationshipChildrenInSection = relationshipFields.flatMap(
      (field) => {
        const relationshipName = text(field.name);
        const childArtifactId = relationshipName
          ? text(relationshipChildren?.[relationshipName])
          : null;
        const child = childArtifactId ? children.get(childArtifactId) : null;
        if (!relationshipName || !child) return [];
        return [
          {
            relationshipName,
            child,
            childLinkPending: relationshipChildLinkPending(
              artifact,
              relationshipName,
              child,
            ),
            childId: `${artifact.artifactId}:child:${stableToken(child.artifactId)}`,
          },
        ];
      },
    );
    const relationshipChildFieldNames = new Set(
      relationshipChildrenInSection.map(
        ({ relationshipName }) => relationshipName,
      ),
    );
    const residualFieldNames = section.fieldNames.filter(
      (fieldName) => !relationshipChildFieldNames.has(fieldName),
    );
    const residualComplete = sectionComplete(artifact, residualFieldNames);
    const residualId = `${id}:details`;
    const childActive = relationshipChildrenInSection.some(
      ({ child, childLinkPending }) =>
        childLinkPending || !TERMINAL_ARTIFACT_STATUSES.has(child.status),
    );
    const childIsWorking = relationshipChildrenInSection.some(
      ({ child, childLinkPending }) =>
        childLinkPending || child.status === "running",
    );
    const sectionStatus: ArtifactWorkflowNodeStatus =
      complete && !blockedByEarlierSection
        ? "completed"
        : blockedByEarlierAction
          ? "upcoming"
          : childActive
            ? childIsWorking
              ? "working"
              : "needs_input"
            : !TERMINAL_ARTIFACT_STATUSES.has(artifact.status)
              ? "needs_input"
              : "upcoming";
    if (!complete) foundIncompleteSection = true;
    nodesById[id] = {
      id,
      artifactId: artifact.artifactId,
      parentId: operationId,
      childIds: [
        ...relationshipChildrenInSection.map(({ childId }) => childId),
        ...(relationshipChildrenInSection.length > 0 &&
        residualFieldNames.length > 0 &&
        !residualComplete
          ? [residualId]
          : []),
      ],
      kind:
        relationshipChildrenInSection.length > 0
          ? "group"
          : relationshipField
            ? "relationship"
            : "input",
      label: section.label,
      description: section.description,
      status: sectionStatus,
      availability:
        sectionStatus === "completed"
          ? "readonly"
          : sectionStatus === "upcoming"
            ? "unavailable"
            : "available",
      fieldNames:
        relationshipChildrenInSection.length > 0
          ? undefined
          : section.fieldNames,
      recap:
        sectionStatus === "completed"
          ? sectionRecap(artifact, section.fieldNames)
          : null,
      completedAt: sectionStatus === "completed" ? artifact.updatedAt : null,
    };
    sectionNodeIds.push(id);

    const childStatuses: ArtifactWorkflowNodeStatus[] = [];
    for (const {
      child,
      childId,
      childLinkPending,
    } of relationshipChildrenInSection) {
      const authoritativeChildStatus: ArtifactWorkflowNodeStatus =
        childLinkPending
          ? "working"
          : child.status === "completed"
            ? "completed"
            : child.status === "failed"
              ? "failed"
              : child.status === "canceled"
                ? "canceled"
                : child.status === "running"
                  ? "working"
                  : child.status === "blocked" || child.status === "stale"
                    ? "blocked"
                    : child.status === "ready"
                      ? "current"
                      : "needs_input";
      const childStatus: ArtifactWorkflowNodeStatus = blockedByEarlierSection
        ? "upcoming"
        : foundActiveLeaf &&
            !isTerminalWorkflowStepStatus(authoritativeChildStatus)
          ? "upcoming"
          : authoritativeChildStatus;
      childStatuses.push(childStatus);
      nodesById[childId] = {
        id: childId,
        artifactId: artifact.artifactId,
        parentId: id,
        childIds: [],
        kind: "child_artifact",
        label: child.label,
        description: child.description,
        status: childStatus,
        availability:
          childStatus === "completed"
            ? "readonly"
            : childStatus === "upcoming"
              ? "unavailable"
              : "available",
        childArtifactId: child.artifactId,
        recap:
          childStatus === "completed"
            ? { summary: `${child.label} completed`, items: [] }
            : null,
        attention:
          childStatus === "failed"
            ? { title: `${child.label} failed`, retryable: true }
            : null,
        startedAt: child.executedAt || null,
        completedAt: childLinkPending ? null : child.completedAt || null,
      };
      if (
        !blockedByEarlierSection &&
        !isArtifactWorkflowNodeTerminal(childStatus)
      ) {
        if (!foundActiveLeaf) {
          currentNodeId = childId;
          foundActiveLeaf = true;
        }
      }
    }
    if (
      relationshipChildrenInSection.length > 0 &&
      residualFieldNames.length > 0 &&
      !residualComplete
    ) {
      const residualStatus: ArtifactWorkflowNodeStatus = residualComplete
        ? "completed"
        : blockedByEarlierSection || foundActiveLeaf
          ? "upcoming"
          : !TERMINAL_ARTIFACT_STATUSES.has(artifact.status)
            ? "needs_input"
            : "upcoming";
      const residualFields = artifactFields(artifact).filter((field) =>
        residualFieldNames.includes(text(field.name) || ""),
      );
      const residualLabel =
        residualFields.length === 1
          ? text(residualFields[0]?.label) || section.label
          : "Details";
      childStatuses.push(residualStatus);
      nodesById[residualId] = {
        id: residualId,
        artifactId: artifact.artifactId,
        parentId: id,
        childIds: [],
        kind: residualFields.some((field) =>
          ["relationshipOne", "relationshipMany"].includes(
            text(field.kind) || "",
          ),
        )
          ? "relationship"
          : "input",
        label: residualLabel,
        description: section.description,
        status: residualStatus,
        availability:
          residualStatus === "completed"
            ? "readonly"
            : residualStatus === "upcoming"
              ? "unavailable"
              : "available",
        fieldNames: residualFieldNames,
        recap: residualComplete
          ? sectionRecap(artifact, residualFieldNames)
          : null,
        completedAt: residualComplete ? artifact.updatedAt : null,
      };
      if (
        !foundActiveLeaf &&
        ["needs_input", "working", "blocked", "failed"].includes(residualStatus)
      ) {
        currentNodeId = residualId;
        foundActiveLeaf = true;
      }
    }
    if (childStatuses.length > 0) {
      // Once a section contains nested actions it becomes a structural
      // summary. Its state follows the first unfinished child; when all child
      // actions are terminal it mirrors the last child as the recap state.
      const representativeChildStatus =
        childStatuses.find(
          (status) => !isArtifactWorkflowNodeTerminal(status),
        ) ?? (complete ? childStatuses.at(-1) : null);
      if (representativeChildStatus) {
        nodesById[id]!.status = representativeChildStatus;
      }
      nodesById[id]!.availability = "readonly";
    }
    if (
      !foundActiveLeaf &&
      relationshipChildrenInSection.length === 0 &&
      ["needs_input", "working", "blocked", "failed"].includes(sectionStatus)
    ) {
      currentNodeId = id;
      foundActiveLeaf = true;
    }
  }

  const transitionsAreGatedByForm =
    (foundIncompleteSection || foundActiveLeaf) &&
    !TERMINAL_ARTIFACT_STATUSES.has(artifact.status);
  for (const step of stateTransitionSteps(artifact)) {
    // Keep internal state-machine bookkeeping in the authoritative plan, but
    // do not present it as a navigable user action. This preserves the
    // workflow's real execution model while keeping the navigation limited to
    // meaningful user-level steps.
    if (!step.userVisible) continue;
    hasVisibleTransition = true;
    const id = `${artifact.artifactId}:transition:${stableToken(step.transition)}`;
    const rawStatus = transitionNodeStatus(artifact, step);
    const status = isTerminalWorkflowStepStatus(rawStatus)
      ? rawStatus
      : transitionsAreGatedByForm || transitionHasActiveStep
        ? "upcoming"
        : firstTransitionStatus(artifact, rawStatus);
    // Form completeness can gate a future transition, but it must not rewrite
    // a persisted completed transition as upcoming. The state-path facts are
    // authoritative for work that already happened. Likewise, once the first
    // unfinished transition is found, later plan flags are future work even if
    // a stale snapshot marked one of them ready or blocked.
    projectedTransitionStatuses.push(status);
    if (!isTerminalWorkflowStepStatus(status)) {
      transitionHasActiveStep = true;
    }
    const blocker = step.blockers[0];
    nodesById[id] = {
      id,
      artifactId: artifact.artifactId,
      parentId: transitionGroupLabel ? transitionGroupId : operationId,
      childIds: [],
      kind: "transition",
      label: step.label,
      description: step.description,
      status,
      availability:
        status === "completed"
          ? "readonly"
          : status === "upcoming"
            ? "unavailable"
            : "available",
      operationPhase:
        step.status === "needs_other_user"
          ? "waiting_for_approval"
          : status === "working"
            ? operationPhase(artifact)
            : null,
      operationDetail:
        text(record(record(artifact.metadata)?.execution)?.message) || null,
      recap:
        status === "completed"
          ? { summary: `${step.label} completed`, items: [] }
          : null,
      attention:
        status === "failed" || status === "blocked"
          ? {
              title: text(blocker?.label) || `${step.label} needs attention`,
              detail:
                text(blocker?.detail) || text(blocker?.description) || null,
              retryable: status === "failed",
            }
          : null,
      startedAt:
        status === "working" ? artifact.executedAt || artifact.updatedAt : null,
      completedAt: status === "completed" ? artifact.updatedAt : null,
    };
    transitionNodeIds.push(id);
    if (
      !currentNodeId &&
      !["completed", "upcoming", "skipped", "canceled"].includes(status)
    ) {
      currentNodeId = id;
    }
  }

  if (transitionNodeIds.length) {
    const transitionNodes = transitionNodeIds
      .map((id) => nodesById[id])
      .filter((node): node is ArtifactWorkflowNode => Boolean(node));
    if (transitionGroupLabel) {
      const groupStatus = workflowGroupStatus(transitionNodes);
      const activeTransition = transitionNodes.find((node) =>
        ["current", "needs_input", "working", "blocked", "failed"].includes(
          node.status,
        ),
      );
      nodesById[transitionGroupId] = {
        id: transitionGroupId,
        artifactId: artifact.artifactId,
        parentId: operationId,
        childIds: transitionNodeIds,
        kind: "group",
        label: transitionGroupLabel,
        description: null,
        status: groupStatus,
        availability:
          groupStatus === "upcoming"
            ? "unavailable"
            : groupStatus === "completed"
              ? "readonly"
              : "available",
        operationPhase: activeTransition?.operationPhase || null,
        startedAt: activeTransition?.startedAt || null,
        completedAt:
          groupStatus === "completed"
            ? transitionNodes.at(-1)?.completedAt || artifact.updatedAt
            : null,
      };
      sectionNodeIds.push(transitionGroupId);
    } else {
      // Older or hand-authored state plans may not carry a customer-facing
      // class label. Keep their transition leaves in their authored order
      // rather than inventing a visible heading from an internal machine key.
      sectionNodeIds.push(...transitionNodeIds);
    }
  }

  const projectedOperationStatus = operationStatus(
    artifact,
    projectedTransitionStatuses,
  );
  // Explicit transition leaves replace the otherwise-visible operation node
  // in the navigation. Carry the operation's authoritative terminal delivery
  // state onto those leaves so the user can still distinguish an effect that
  // was applied from one whose result has reached the graph. Do not infer
  // propagation for an unfinished or partially written state path.
  if (projectedOperationStatus === "completed") {
    for (const transitionNodeId of transitionNodeIds) {
      const transition = nodesById[transitionNodeId];
      if (
        transition?.kind === "transition" &&
        transition.status === "completed"
      ) {
        transition.completionState = "graph_propagated";
      }
    }
  }
  const phase = operationPhase(artifact);
  const activeDescendantStatus = currentNodeId
    ? nodesById[currentNodeId]?.status
    : null;
  // A structural parent mirrors the leaf the user is actually dealing with.
  // This is deliberately based on the canonical current node, never on the
  // position of a row in the rendered tree.
  const finalStatus: ArtifactWorkflowNodeStatus =
    activeDescendantStatus &&
    !isTerminalWorkflowStepStatus(activeDescendantStatus)
      ? activeDescendantStatus
      : projectedOperationStatus;
  const validationStatus: ArtifactWorkflowNodeStatus = currentNodeId
    ? "upcoming"
    : projectedOperationStatus === "upcoming"
      ? "current"
      : projectedOperationStatus;
  nodesById[validationId] = {
    id: validationId,
    artifactId: artifact.artifactId,
    parentId: operationId,
    childIds: [],
    kind: "operation",
    label: operationLabel(artifact),
    description: artifact.description || null,
    status: validationStatus,
    availability:
      validationStatus === "upcoming"
        ? "unavailable"
        : validationStatus === "completed"
          ? "readonly"
          : "available",
    operationPhase: phase,
    completionState:
      projectedOperationStatus === "completed"
        ? "graph_propagated"
        : phase === "synchronizing" || phase === "verifying"
          ? "effect_applied"
          : null,
    operationDetail: text(
      record(record(artifact.metadata)?.execution)?.message,
    ),
    recap:
      validationStatus === "completed"
        ? { summary: `${artifact.label} completed`, items: [] }
        : null,
    attention:
      validationStatus === "failed"
        ? { title: `${artifact.label} failed`, retryable: true }
        : validationStatus === "blocked"
          ? {
              title: `${artifact.label} needs attention`,
              retryable: artifact.status === "stale",
            }
          : null,
    startedAt: artifact.executedAt || null,
    completedAt: artifact.completedAt || null,
  };
  sectionNodeIds.push(validationId);
  if (
    !currentNodeId &&
    !hasVisibleTransition &&
    !["completed", "canceled"].includes(finalStatus)
  ) {
    currentNodeId = validationId;
  }
  const rootStatus: ArtifactWorkflowNodeStatus =
    currentNodeId === validationId
      ? nodesById[validationId]!.status
      : finalStatus;
  nodesById[operationId] = {
    id: operationId,
    artifactId: artifact.artifactId,
    parentId: null,
    childIds: sectionNodeIds,
    kind: "group",
    label: artifact.label,
    description: artifact.description || null,
    status: rootStatus,
    availability:
      rootStatus === "upcoming"
        ? "unavailable"
        : rootStatus === "completed"
          ? "readonly"
          : "available",
    operationPhase: null,
    startedAt: artifact.executedAt || null,
    completedAt: artifact.completedAt || null,
  };
  rootNodeIds.push(operationId);

  const candidateBeforeTarget: ArtifactWorkflow = {
    schemaVersion: 1,
    revision: artifact.workflow?.revision || 1,
    artifactId: artifact.artifactId,
    rootNodeIds,
    nodesById,
    currentNodeId: null,
    updatedAt: now,
  };
  currentNodeId =
    getArtifactWorkflowNavigationTarget(candidateBeforeTarget)?.id || null;
  const automaticTarget = currentNodeId
    ? nodesById[currentNodeId] || null
    : null;
  const canonicalRootStatus =
    automaticTarget && !isArtifactWorkflowNodeTerminal(automaticTarget)
      ? automaticTarget.status
      : projectedOperationStatus;
  nodesById[operationId]!.status = canonicalRootStatus;
  nodesById[operationId]!.availability =
    canonicalRootStatus === "upcoming"
      ? "unavailable"
      : canonicalRootStatus === "completed"
        ? "readonly"
        : "available";
  const candidate: ArtifactWorkflow = {
    ...candidateBeforeTarget,
    currentNodeId,
  };
  if (
    artifact.workflow &&
    JSON.stringify(semanticWorkflow(artifact.workflow)) !==
      JSON.stringify(semanticWorkflow(candidate))
  ) {
    candidate.revision = artifact.workflow.revision + 1;
  } else if (artifact.workflow) {
    candidate.updatedAt = artifact.workflow.updatedAt;
  }
  return candidate;
}

export function getCurrentArtifactWorkflowNode(
  workflow: ArtifactWorkflow,
): ArtifactWorkflowNode | null {
  return workflow.currentNodeId
    ? workflow.nodesById[workflow.currentNodeId] || null
    : null;
}

export function getArtifactWorkflowNodes(
  workflow: ArtifactWorkflow,
): ArtifactWorkflowNode[] {
  const visit = (id: string): ArtifactWorkflowNode[] => {
    const node = workflow.nodesById[id];
    return node ? [node, ...node.childIds.flatMap(visit)] : [];
  };
  return workflow.rootNodeIds.flatMap(visit);
}

export function canInspectArtifactWorkflowNode(
  node: ArtifactWorkflowNode,
): boolean {
  return node.childIds.length === 0 && node.availability !== "unavailable";
}

export function assertArtifactWorkflow(workflow: ArtifactWorkflow): void {
  if (workflow.schemaVersion !== 1)
    throw new Error("Unsupported artifact workflow schema");
  if (!Number.isSafeInteger(workflow.revision) || workflow.revision < 1) {
    throw new Error("Artifact workflow revision must be a positive integer");
  }
  const visited = new Set<string>();
  const visiting = new Set<string>();
  const nodes: ArtifactWorkflowNode[] = [];
  const visit = (id: string, expectedParentId: string | null) => {
    const node = workflow.nodesById[id];
    if (!node) throw new Error(`Artifact workflow node ${id} does not exist`);
    if (visiting.has(id)) throw new Error("Artifact workflow contains a cycle");
    if (visited.has(id))
      throw new Error("Artifact workflow contains a duplicate node reference");
    if (node.parentId !== expectedParentId) {
      throw new Error(`Artifact workflow node ${id} has an invalid parent`);
    }
    visiting.add(id);
    nodes.push(node);
    node.childIds.forEach((childId) => visit(childId, id));
    visiting.delete(id);
    visited.add(id);
  };
  workflow.rootNodeIds.forEach((id) => visit(id, null));
  if (visited.size !== Object.keys(workflow.nodesById).length) {
    throw new Error("Artifact workflow contains unreachable nodes");
  }
  if (workflow.currentNodeId && !workflow.nodesById[workflow.currentNodeId]) {
    throw new Error("Artifact workflow current node does not exist");
  }
  const currentNode = workflow.currentNodeId
    ? workflow.nodesById[workflow.currentNodeId]
    : null;
  if (currentNode && currentNode.childIds.length > 0) {
    throw new Error("Artifact workflow current node must be actionable leaf");
  }
  const actionable = nodes.filter(
    (node) =>
      isArtifactWorkflowNodeNavigableLeaf(workflow, node) &&
      isArtifactWorkflowNodeActionable(node),
  );
  if (actionable.length > 1)
    throw new Error("Artifact workflow has multiple current nodes");
  if (actionable.length === 1 && actionable[0]!.id !== workflow.currentNodeId) {
    throw new Error("Artifact workflow current node does not own its action");
  }
  if (
    workflow.currentNodeId &&
    !actionable.length &&
    !isArtifactWorkflowNodeTerminal(currentNode!)
  ) {
    throw new Error("Artifact workflow current node is not actionable");
  }
}

export function requireArtifactWorkflow<T extends ArtifactLike>(
  artifact: T,
): T {
  if (!artifact.workflow) {
    throw new Error(
      `Artifact ${artifact.artifactId} has no canonical workflow`,
    );
  }
  assertArtifactWorkflow(artifact.workflow);
  if (artifact.workflow.artifactId !== artifact.artifactId) {
    throw new Error("Artifact workflow belongs to a different artifact");
  }
  return artifact;
}

export function requireArtifactWorkflowResult<
  T extends { artifact: ArtifactLike },
>(result: T): T {
  requireArtifactWorkflow(result.artifact);
  return result;
}
